import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MentorAvatar } from "@/components/mentor/MentorAvatar";
import { MentorGamesBadges } from "@/components/mentor/MentorGamesBadges";
import { MentorRatingBadge } from "@/components/mentor/MentorRatingBadge";
import { formatPrice } from "@/utils/mentorUtils";
import { ChevronDown, ChevronUp, Crown, Trophy, Star } from "lucide-react";

interface MentorCardProps {
  id: string;
  slug?: string | null;
  imageUrl: string | null;
  coverUrl: string | null;
  name: string;
  bio: string | null;
  averageRating: number;
  minimumPrice: number | null;
  games: { id: string; name: string }[];
  isFeatured?: boolean;
  position?: number;
}

export const MentorCard: React.FC<MentorCardProps> = ({
  id,
  slug,
  imageUrl,
  coverUrl,
  name,
  bio,
  averageRating,
  minimumPrice,
  games,
  isFeatured = false,
  position,
}) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  
  const handleViewProfile = () => {
    try {
      // Usar slug se disponível, senão usar ID
      const urlPath = slug ? `/mentores/${slug}` : `/mentores/${id}`;
      console.log(`Navigating to mentor profile: ${urlPath}`);
      navigate(urlPath);
    } catch (error) {
      console.error(`Error navigating to mentor profile ${id}:`, error);
    }
  };

  const toggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  // Make sure we always display a valid name - but prioritize the actual name
  const displayName = name && name.trim() !== "" ? name : `Mentor ${id.substring(0, 4)}`;

  console.log(`Rendering MentorCard for ${id} with name: "${name}", displayName: "${displayName}"`);

  // Função para truncar a descrição
  const getShortBio = (text: string, maxLength: number = 80) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Classes de destaque
  const featuredClasses = isFeatured ? (() => {
    switch (position) {
      case 1: return 'border-2 border-yellow-500 shadow-yellow-500/20 shadow-lg';
      case 2: return 'border-2 border-gray-400 shadow-gray-400/20 shadow-lg';
      case 3: return 'border-2 border-amber-600 shadow-amber-600/20 shadow-lg';
      default: return '';
    }
  })() : '';

  const getPositionIcon = () => {
    if (!isFeatured || !position) return null;
    switch (position) {
      case 1: return <Crown className="h-4 w-4 text-yellow-500" />;
      case 2: return <Trophy className="h-4 w-4 text-gray-400" />;
      case 3: return <Star className="h-4 w-4 text-amber-600" />;
      default: return null;
    }
  };

  return (
    <div className={`w-full max-w-sm rounded-xl overflow-hidden shadow-lg border bg-[#181D24] hover:scale-105 transition-transform duration-300 relative ${featuredClasses || 'border-[#223342]'}`}>
      
      {/* Badge de posição para mentores em destaque */}
      {isFeatured && position && (
        <div className="absolute -top-2 -right-2 z-30 bg-gradient-to-r from-[#00B6F0] to-[#0099CC] rounded-full p-2 shadow-lg">
          {getPositionIcon()}
        </div>
      )}

      {/* Cover Image with Gradient Overlay */}
      <div className="relative h-36">
        <div className="absolute inset-0">
          <img
            src={coverUrl || "/placeholder.svg"}
            alt={`${displayName} cover`}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.src = "/placeholder.svg";
              console.log(`Failed to load cover image for mentor ${id}`);
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#18222F]/50 via-[#18222F]/70 to-[#181D24] z-10"></div>
        </div>

        {/* Rating Badge */}
        <div className="absolute top-3 right-3 z-20">
          <MentorRatingBadge rating={averageRating} />
        </div>
      </div>

      {/* Avatar positioned to overlap the banner properly from the center */}
      <div className="relative -mt-10 flex justify-center">
        <MentorAvatar 
          imageUrl={imageUrl} 
          name={displayName} 
          size="md" 
          id={id} 
          className="z-20 border-[#181D24]"
        />
      </div>
      
      {/* Content */}
      <div className="px-5 pt-4 pb-5">
        {/* Mentor Name and Price */}
        <div className="text-center mb-3">
          <h3 className="text-xl font-bold text-white">{displayName}</h3>
          {minimumPrice !== null && (
            <p className="text-sm text-gray-200 font-medium">
              Mentoria a partir de <span className="text-[#00B6F0]">{formatPrice(minimumPrice)}</span>
            </p>
          )}
        </div>

        {/* Bio */}
        {bio && (
          <div className="mb-4">
            <p className="text-gray-300 text-sm">
              {isExpanded ? bio : getShortBio(bio)}
            </p>
            {bio.length > 80 && (
              <button
                onClick={toggleExpanded}
                className="text-[#00B6F0] text-xs hover:text-[#00C7FF] transition-colors mt-1 flex items-center gap-1"
              >
                {isExpanded ? (
                  <>
                    Ver menos <ChevronUp className="w-3 h-3" />
                  </>
                ) : (
                  <>
                    Ver mais <ChevronDown className="w-3 h-3" />
                  </>
                )}
              </button>
            )}
          </div>
        )}
        
        {/* Games Tags */}
        <div className="mb-4">
          <MentorGamesBadges games={games} />
        </div>
        
        {/* View Profile Button */}
        <Button 
          onClick={handleViewProfile}
          className="w-full bg-gradient-to-r from-[#00B6F0] to-[#0099CC] hover:from-[#00C7FF] hover:to-[#00AADD] text-black font-medium"
        >
          Ver Perfil
        </Button>
      </div>
    </div>
  );
};
